import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import moment from 'moment';
import {
  Button,
  Spinner,
  SubmitButton,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { promptToaster } from '../../../App/actions';
import CreateScheduledReportForm from './CreateScheduledReportForm';
import ScheduledReportService from '../../../../services/scheduledReportService';
import PropertyGroupService from '../../../../services/propertyGroupService';
import ReportService from '../../../../services/reportService';
import useFormatMessage from '../../../../hooks/useFormatMessage';
import { CSV_ONLY_REPORT_REFERENCE_IDS } from './constants';
import {
  computeFirstExecutionDateTime,
  computeNextExecutionDateTimeFromNow,
} from './dateRangeValidation';
import { convert12hTo24h } from './timeFormat';
import messages from './messages';

const scheduledReportService = new ScheduledReportService();
const reportService = new ReportService();

// Extract a user-friendly error message from a thrown Error.
// `utils/api.js` throws `Error("HTTP <status>: <body>")`, where `<body>` is the
// raw response body (typically a JSON string like
// `{name, message, httpCode, ...}`).
//
// Returns the parsed `message` field from JSON bodies in the shape
// `HTTP <status>: <json>`. Returns `error.message` as-is when the message does
// not match that shape (genuine non-HTTP errors stay visible). Returns
// `fallback` when:
//   - error or error.message is missing,
//   - the body parses as JSON but has no non-empty `message` field,
//   - the body is `HTTP <status>:` followed by non-JSON text.
//
// The capture group uses `[\s\S]+` (not `.+`) so multi-line bodies — e.g. a
// JSON payload pretty-printed across newlines, or a body that embeds a stack
// trace — are captured in full. `.+` would stop at the first newline and the
// JSON.parse below would silently fail.
const extractBackendErrorMessage = (error, fallback) => {
  const raw = error?.message;
  if (!raw) return fallback;
  const match = raw.match(/^HTTP \d+:\s*([\s\S]+)$/);
  if (!match) return raw;
  try {
    const body = JSON.parse(match[1]);
    if (body && typeof body.message === 'string' && body.message.length > 0) {
      return body.message;
    }
  } catch (_e) {
    // body is not JSON; fall through to the fallback message rather than
    // showing a raw, non-user-friendly string in the toast.
  }
  return fallback;
};

// Helper to convert 24h to 12h format
const convert24hTo12h = (time24h) => {
  if (!time24h) return { time: '12:00', amPm: 'AM' };
  const [hourStr, minutes] = time24h.split(':');
  let hour = parseInt(hourStr, 10);
  let amPm = 'AM';

  if (hour >= 12) {
    amPm = 'PM';
    if (hour > 12) hour -= 12;
  } else if (hour === 0) {
    hour = 12;
  }

  return { time: `${hour}:${minutes}`, amPm };
};

// Helper to create ISO datetime string with timezone offset
// Example output: "2024-02-19T14:30:00-05:00"
const createISODateTime = (dateString, time24h) => {
  const dateMoment = moment(dateString, 'MM/DD/YYYY');
  const [hours, minutes] = time24h.split(':').map(Number);

  const dateTime = dateMoment
    .hours(hours)
    .minutes(minutes)
    .seconds(0)
    .milliseconds(0);

  // Returns ISO string with timezone offset (e.g., "2024-02-19T14:30:00-05:00")
  return dateTime.format();
};

// Helper to derive the reportingPeriod radio value from a saved schedule.
// Priority order:
//   1. dateRangeType === 'finishedDate' -> 'periodToDate'
//   2. dateRangeType === 'createdAt'    -> 'asOfDate'
//   3. runByPeriod === true             -> 'periodToDate' (legacy fallback)
//   4. otherwise -> undefined, which allows create mode to auto-derive from
//      report metadata, while edit mode may fall back to 'asOfDate' and
//      preserve that existing value
const deriveReportingPeriodFromSchedule = (schedule) => {
  if (!schedule) return undefined;
  if (schedule.dateRangeType === 'finishedDate') return 'periodToDate';
  if (schedule.dateRangeType === 'createdAt') return 'asOfDate';
  if (schedule.runByPeriod === true) return 'periodToDate';
  return undefined;
};

const CreateScheduledReportModal = ({
  isOpen,
  closeModal,
  organizationId,
  userId,
  userProperties = [],
  promptToaster,
  onScheduleCreated,
  // Edit mode props
  schedule = null,
  onScheduleUpdated,
}) => {
  const isEditMode = !!schedule;
  const [isFormValid, setIsFormValid] = useState(false);
  const [hasDuplicateEmails, setHasDuplicateEmails] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const formRef = useRef(null);
  const queryClient = useQueryClient();
  const formatMessage = useFormatMessage();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedReport(null);
      setIsFormValid(false);
      setHasDuplicateEmails(false);
    }
  }, [isOpen]);

  // Fetch available reports (both create and edit mode)
  const { data: reportsData = [], isLoading: isLoadingReports } = useQuery(
    ['CentralizedReports', 'definitions', organizationId, 'all'],
    () =>
      reportService.getCentralizedReportDefinitions({
        organizationId,
        all: true,
      }),
    {
      enabled: isOpen && !!organizationId,
      refetchOnWindowFocus: false,
      onError: () => {
        promptToaster({
          type: 'error',
          message: 'Error fetching available reports',
          title: 'Error',
        });
      },
    },
  );

  // Transform reports data to the expected format
  const availableReports = useMemo(() => {
    const items = reportsData?.results || reportsData || [];
    if (!Array.isArray(items)) return [];
    return items.map((item) => ({
      id: item.id,
      name: item.name,
      referenceId: item.referenceId || item.id,
      version: item.version || 'legacy',
      configuration: item.configuration,
      monthendCSV: item.monthendCSV,
      monthendPDF: item.monthendPDF,
      isPropertyLevel: item.isPropertyLevel,
      runByDate: item.runByDate,
      runByPeriod: item.runByPeriod,
    }));
  }, [reportsData]);

  // Set selected report from schedule in edit mode
  // Uses availableReports to get full configuration (including formats)
  // Match by id first, then by name as fallback (legacy schedules use PostgreSQL IDs
  // which differ from the MongoDB IDs returned by centralized report definitions)
  const matchedReportFromDefinitions = useMemo(() => {
    if (!isEditMode || !schedule) return null;
    return (
      availableReports.find((r) => r.id === schedule.reportId) ||
      availableReports.find((r) => r.name === schedule.reportName) ||
      null
    );
  }, [isEditMode, schedule, availableReports]);

  useEffect(() => {
    if (isEditMode && schedule && isOpen && !isLoadingReports) {
      const reportFromSchedule = matchedReportFromDefinitions || {
        id: schedule.reportId,
        name: schedule.reportName,
        version: schedule.reportVersion || 'new',
        // Build minimal format config from the schedule's fileType so the
        // format dropdown includes the saved value even if the query failed
        ...(schedule.fileType === 'pdf'
          ? { monthendPDF: true, monthendCSV: false }
          : schedule.fileType === 'csv'
          ? { monthendCSV: true, monthendPDF: false }
          : {}),
      };
      setSelectedReport(reportFromSchedule);
    }
  }, [
    isEditMode,
    schedule,
    isOpen,
    isLoadingReports,
    matchedReportFromDefinitions,
  ]);

  // Prepare initial values for edit mode
  const initialValues = useMemo(() => {
    if (!isEditMode || !schedule) return null;

    const { time, amPm } = convert24hTo12h(schedule.executionTime);

    // Get property IDs from schedule
    const propertyIds = schedule.properties
      ? schedule.properties.map((p) => p.id)
      : schedule.propertyIds || [];

    // Convert executionDate from YYYY-MM-DD to MM/DD/YYYY for form
    let executionDateFormatted = null;
    if (schedule.executionDate) {
      executionDateFormatted = moment(
        schedule.executionDate,
        'YYYY-MM-DD',
      ).format('MM/DD/YYYY');
    }

    // Convert startDate and endDate from YYYY-MM-DD to MM/DD/YYYY for form
    let startDateFormatted = null;
    if (schedule.startDate) {
      startDateFormatted = moment(schedule.startDate, 'YYYY-MM-DD').format(
        'MM/DD/YYYY',
      );
    }
    let endDateFormatted = null;
    if (schedule.endDate) {
      endDateFormatted = moment(schedule.endDate, 'YYYY-MM-DD').format(
        'MM/DD/YYYY',
      );
    }

    return {
      report: schedule.reportId,
      properties: propertyIds,
      format: schedule.fileType || 'csv',
      frequency: schedule.frequency || 'daily',
      dayOfWeek: schedule.dayOfWeek?.toString(),
      dayOfMonth: schedule.dayOfMonth?.toString(),
      executionDate: executionDateFormatted,
      startDate: startDateFormatted,
      endDate: endDateFormatted,
      executionTime: time,
      amPm: amPm,
      targetEmails: schedule.targetEmails || [],
      reportingPeriod: deriveReportingPeriodFromSchedule(schedule),
      leaseType: schedule.leaseType || '',
    };
  }, [isEditMode, schedule]);

  // Get configuration from selected report
  const configuration = selectedReport?.configuration;

  // Get legacy format fields from selected report
  // For legacy property-level reports, show CSV and PDF by default
  // (consistent with property-level Create Report modal behavior)
  // except for reports that only support CSV (no PDF)
  const isLegacyPropertyLevel =
    selectedReport?.version === 'legacy' && selectedReport?.isPropertyLevel;
  const isCsvOnlyReport = CSV_ONLY_REPORT_REFERENCE_IDS.includes(
    selectedReport?.referenceId,
  );
  const legacyFormatFields = isLegacyPropertyLevel
    ? { monthendCSV: true, monthendPDF: !isCsvOnlyReport }
    : {
        monthendCSV: selectedReport?.monthendCSV,
        monthendPDF: selectedReport?.monthendPDF,
      };

  // Fetch property groups
  const { data: userPropertyGroups = [] } = useQuery(
    ['userPropertyGroups', organizationId, userId],
    async () => {
      const propertyGroupService = new PropertyGroupService();
      const groups = await propertyGroupService.getUserPropertyGroups(
        organizationId,
      );
      return groups || [];
    },
    {
      enabled:
        isOpen &&
        configuration?.propertyGroupsFilter === true &&
        !!organizationId &&
        !!userId,
      refetchOnWindowFocus: false,
      onError: () => {
        promptToaster({
          type: 'error',
          message: 'Error fetching property groups',
          title: 'Error',
        });
      },
    },
  );

  // Create mutation
  const createScheduleMutation = useMutation(
    (payload) => scheduledReportService.createSchedule(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['ScheduledReports']);
        promptToaster({
          type: 'success',
          message: formatMessage(messages.scheduleSuccess),
          title: 'Success',
        });
        if (onScheduleCreated) {
          onScheduleCreated();
        }
        handleClose();
      },
      onError: (error) => {
        promptToaster({
          type: 'error',
          message: extractBackendErrorMessage(
            error,
            formatMessage(messages.scheduleError),
          ),
          title: 'Error',
        });
      },
    },
  );

  // Update mutation
  const updateScheduleMutation = useMutation(
    ({ id, params }) => scheduledReportService.updateSchedule(id, params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['ScheduledReports']);
        promptToaster({
          type: 'success',
          message: formatMessage(messages.updateSuccess),
          title: 'Success',
        });
        if (onScheduleUpdated) {
          onScheduleUpdated();
        }
        handleClose();
      },
      onError: (error) => {
        promptToaster({
          type: 'error',
          message: extractBackendErrorMessage(
            error,
            formatMessage(messages.updateError),
          ),
          title: 'Error',
        });
      },
    },
  );

  const isLoading =
    createScheduleMutation.isLoading || updateScheduleMutation.isLoading;

  const handleValidationChange = useCallback((isValid) => {
    setIsFormValid(isValid);
  }, []);

  const handleReportChange = useCallback((report) => {
    setSelectedReport(report);
  }, []);

  const handleDuplicateEmailChange = useCallback((hasDuplicates) => {
    setHasDuplicateEmails(hasDuplicates);
  }, []);

  // Helper function to validate that a scheduled date/time is at least 10 minutes in the future
  // This is required by AWS EventBridge Scheduler (minimum 5 minutes, we use 10 for safety).
  //
  // In CREATE mode we compare against `computeFirstExecutionDateTime`: the very
  // first occurrence resolved from startDate + dayOfWeek/dayOfMonth + time.
  //
  // In EDIT mode (ENG-27666) we compare against `computeNextExecutionDateTimeFromNow`:
  // the next future occurrence as of "now". For recurring schedules whose
  // startDate is in the past, the first-execution value is always in the past
  // and would incorrectly trigger the "in the past" error whenever the user
  // edits an unrelated field like endDate. The next-execution helper rolls
  // forward to the next valid cycle (next day / week / month) when needed.
  const validateScheduleDateTimeForToday = useCallback(
    (formValues, executionTime24h) => {
      const nextExec = isEditMode
        ? computeNextExecutionDateTimeFromNow({
            frequency: formValues.frequency,
            startDate: formValues.startDate,
            executionDate: formValues.executionDate,
            executionTime24h,
            dayOfWeek: formValues.dayOfWeek,
            dayOfMonth: formValues.dayOfMonth,
          })
        : computeFirstExecutionDateTime({
            frequency: formValues.frequency,
            startDate: formValues.startDate,
            executionDate: formValues.executionDate,
            executionTime24h,
            dayOfWeek: formValues.dayOfWeek,
            dayOfMonth: formValues.dayOfMonth,
          });

      // Invalid/incomplete inputs: other validators cover those scenarios;
      // do not block here.
      if (!nextExec) return true;

      const minAllowedTime = moment().add(10, 'minutes');
      if (nextExec.isBefore(minAllowedTime)) {
        promptToaster({
          type: 'error',
          message: formatMessage(messages.startDateInPast),
          title: 'Validation Error',
        });
        return false;
      }
      return true;
    },
    [promptToaster, formatMessage, isEditMode],
  );

  const handleSubmit = useCallback(
    (formValues) => {
      const executionTime24h = convert12hTo24h(
        formValues.executionTime,
        formValues.amPm,
      );

      // Validate that the FIRST real execution is at least 10 minutes in the
      // future. The helper resolves the actual first occurrence based on the
      // frequency + dayOfWeek/dayOfMonth (e.g. weekly Wed -> Fri rolls forward).
      if (!validateScheduleDateTimeForToday(formValues, executionTime24h)) {
        return;
      }

      const basePayload = {
        frequency: formValues.frequency,
        executionTime: executionTime24h,
        timezone: formValues.timezone,
        propertyIds: Array.isArray(formValues.properties)
          ? formValues.properties
          : [formValues.properties],
        targetEmails: formValues.targetEmails,
      };

      // Add conditional fields based on frequency
      if (
        formValues.frequency === 'weekly' &&
        formValues.dayOfWeek !== undefined &&
        formValues.dayOfWeek !== ''
      ) {
        basePayload.dayOfWeek = parseInt(formValues.dayOfWeek, 10);
      }
      if (formValues.frequency === 'monthly' && formValues.dayOfMonth) {
        basePayload.dayOfMonth = parseInt(formValues.dayOfMonth, 10);
      }
      if (formValues.frequency === 'one-time' && formValues.executionDate) {
        // Legacy format (for backward compatibility)
        basePayload.executionDate = moment(
          formValues.executionDate,
          'MM/DD/YYYY',
        ).format('YYYY-MM-DD');
        // New format: ISO datetime string with timezone offset
        basePayload.executionDateTime = createISODateTime(
          formValues.executionDate,
          executionTime24h,
        );
      }

      // Add report-specific fields
      if (formValues.format) {
        basePayload.fileType = formValues.format;
      }
      // Always send dateRangeType when reportingPeriod is set
      if (formValues.reportingPeriod) {
        basePayload.dateRangeType =
          formValues.reportingPeriod === 'asOfDate'
            ? 'createdAt'
            : 'finishedDate';
      }
      if (formValues.propertyGroup) {
        basePayload.propertyGroupId = formValues.propertyGroup;
      }
      // The clear-on-switch effect in CreateScheduledReportForm empties
      // leaseType for non-Lease-Change-Out reports, so this only sends it
      // for that report. On edit the report is fixed. (ENG-27547)
      if (formValues.leaseType) {
        basePayload.leaseType = formValues.leaseType;
      }

      // Add startDate and endDate for recurring schedules
      if (formValues.frequency !== 'one-time') {
        if (formValues.startDate) {
          // Legacy format (for backward compatibility)
          basePayload.startDate = moment(
            formValues.startDate,
            'MM/DD/YYYY',
          ).format('YYYY-MM-DD');
          // New format: ISO datetime string with timezone offset
          basePayload.startDateTime = createISODateTime(
            formValues.startDate,
            executionTime24h,
          );
        }
        if (formValues.endDate) {
          // Legacy format (for backward compatibility)
          basePayload.endDate = moment(formValues.endDate, 'MM/DD/YYYY').format(
            'YYYY-MM-DD',
          );
          // New format: ISO datetime string with timezone offset (end of day)
          basePayload.endDateTime = createISODateTime(
            formValues.endDate,
            '23:59',
          );
        } else if (isEditMode) {
          // Explicitly set to null to clear the end date when editing
          // Backend will filter out null values before sending to EventBridge
          basePayload.endDate = null;
          basePayload.endDateTime = null;
        }
      }

      if (isEditMode) {
        updateScheduleMutation.mutate({ id: schedule.id, params: basePayload });
      } else {
        const submittedReportId = formValues.report || selectedReport?.id;
        // Resolve report metadata from availableReports by the submitted id to
        // avoid relying on stale selectedReport when the user changes the
        // selection and submits before the onReportChange effect has flushed.
        const reportFromList =
          availableReports.find((r) => r.id === submittedReportId) || null;
        const resolvedReport = reportFromList || selectedReport;
        const createPayload = {
          ...basePayload,
          organizationId,
          reportId: submittedReportId,
          reportVersion: resolvedReport?.version || 'new',
          isPropertyLevel: resolvedReport?.isPropertyLevel,
          runByPeriod: resolvedReport?.runByPeriod === true,
        };
        createScheduleMutation.mutate(createPayload);
      }
    },
    [
      isEditMode,
      schedule,
      organizationId,
      selectedReport,
      availableReports,
      createScheduleMutation,
      updateScheduleMutation,
      validateScheduleDateTimeForToday,
    ],
  );

  const handleSaveClick = useCallback(() => {
    if (formRef.current) {
      formRef.current.submit();
    }
  }, []);

  const handleClose = useCallback(() => {
    setIsFormValid(false);
    setSelectedReport(null);
    closeModal();
  }, [closeModal]);

  const isSaveDisabled = !isFormValid || isLoading || hasDuplicateEmails;
  const saveTooltip = hasDuplicateEmails
    ? formatMessage(messages.removeDuplicateEmails)
    : undefined;

  // Render edit mode actions
  const editModeActions = (
    <>
      <Button variant="text" onClick={handleClose} disabled={isLoading}>
        {formatMessage(messages.cancel)}
      </Button>
      <SubmitButton
        variant="primary"
        onClick={handleSaveClick}
        disabled={isSaveDisabled}
        isSubmitting={updateScheduleMutation.isLoading}
        tooltip={saveTooltip}
      >
        {formatMessage(messages.saveChanges)}
      </SubmitButton>
    </>
  );

  // Render create mode actions
  const createModeActions = (
    <>
      <Button variant="text" onClick={handleClose} disabled={isLoading}>
        {formatMessage(messages.cancel)}
      </Button>
      <SubmitButton
        variant="primary"
        onClick={handleSaveClick}
        disabled={isSaveDisabled}
        isSubmitting={createScheduleMutation.isLoading}
        tooltip={saveTooltip}
      >
        {formatMessage(messages.save)}
      </SubmitButton>
    </>
  );

  return (
    <Modal
      actions={isEditMode ? editModeActions : createModeActions}
      open={isOpen}
      onClose={handleClose}
      title={
        isEditMode
          ? formatMessage(messages.editSchedule)
          : formatMessage(messages.scheduleReport)
      }
      ModalProps={{
        maxWidth: 'sm',
        fullWidth: true,
      }}
    >
      {isEditMode && isLoadingReports ? (
        <Spinner />
      ) : (
        <CreateScheduledReportForm
          ref={formRef}
          organizationId={organizationId}
          configuration={configuration}
          legacyFormatFields={legacyFormatFields}
          userProperties={userProperties}
          userPropertyGroups={userPropertyGroups}
          availableReports={availableReports}
          onValidationChange={handleValidationChange}
          onSubmit={handleSubmit}
          onReportChange={handleReportChange}
          onDuplicateEmailChange={handleDuplicateEmailChange}
          isEditMode={isEditMode}
          initialValues={initialValues}
          reportName={schedule?.reportName}
          scheduleId={schedule?.id}
        />
      )}
    </Modal>
  );
};

export default connect(null, { promptToaster })(CreateScheduledReportModal);
